import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import { Proposal } from "../../../definitions/proposal";
import { toProposal } from "../../../definitions/proposal-summary";
import { ExchangeService } from "../../../services/api/exchange.service";
import { ProposalsV2Service } from "../../../services/api/proposals-v2.service";
import { AppConfigService } from "../../../services/app.config.service";
import { ExchangeActions } from "../../actions/exchange.actions";
import { FailState } from "../../models/fail-state";
import { ProposalsState } from "../../models/proposals-state";
import { AppState } from "../../reducers";
import { ProposalSelectors } from "../../selectors/proposal.selectors";

@Injectable()
export class ExchangeProposalsGetEffects {
	constructor(
		private actions: Actions,
		private store: Store<AppState>,
		private proposalsV2Service: ProposalsV2Service,
		private appConfigService: AppConfigService,
		private vendorProposalService: ExchangeService
	) {}

	get = createEffect(() =>
		this.actions.pipe(
			ofType(ExchangeActions.proposals.get),
			withLatestFrom(this.store.select(ProposalSelectors.proposals)),
			switchMap(([action, state]) =>
				this.canUseStoreValue(state)
					? of(ExchangeActions.proposals.getSuccess(state))
					: of(ExchangeActions.proposals.getApi(action))
			)
		)
	);
	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(
				ExchangeActions.proposals.getApi,
				ExchangeActions.proposal.updateSuccess,
				ExchangeActions.proposal.createSuccess
			),
			switchMap((action) =>
				this.getProposals(action).pipe(
					map((proposals) =>
						ExchangeActions.proposals.getSuccess({
							proposals,
							loading: false,
							allRetrieved: true
						})
					),
					catchError((error) =>
						of(ExchangeActions.proposals.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	getProposals(action): Observable<Proposal[]> {
		const useV2ProposalsEndpoint = this.appConfigService.getConfigValue(
			"USE_V2_VENDOR_PROPOSALS_ENDPOINT"
		);
		return useV2ProposalsEndpoint
			? this.buildV2ProposalsStream(action)
			: this.vendorProposalService.proposals(
					action.orderIds?.length > 0 ? action.orderIds[0] : null
				);
	}

	/**
	 * The v2Model is flat, map it to a v1 model because that is what the UI uses.
	 * This way we can get the performance benefits of the new call without having to
	 * update all the components.
	 */
	buildV2ProposalsStream(filters): Observable<Proposal[]> {
		return this.proposalsV2Service.proposals(filters).pipe(
			map((proposalSummaries) => {
				return proposalSummaries.map((proposalSummary) => toProposal(proposalSummary));
			})
		);
	}

	private canUseStoreValue(state: ProposalsState): boolean {
		return !_.has(state, "error") && !state.loading && state.allRetrieved;
	}
}
